import { IStore } from 'redux/IStore';
import { IArticleTagsData, IArticleTagsAction } from 'models/magazine/article/articletags';
import { IArticleTagDomainValue } from 'models/magazine/article/articletagdomainvalue';
import * as i18n from 'i18next';
import { actions as notifActions } from 'redux-notifications';
import { IPage } from 'models/common';
const { notifSend } = notifActions;

/** Action Types */
export const GET_REQUEST: string = 'articletags/GET_REQUEST';
export const GET_SUCCESS: string = 'articletags/GET_SUCCESS';
export const GET_FAILURE: string = 'articletags/GET_FAILURE';
export const SET_ARTICLETAGS: string = 'articletags/SET_ARTICLETAGS';
export const SET_LANGUAGE_FILTER: string = 'articletags/SET_LANGUAGE_FILTER';
export const SET_SEARCH_FILTER: string = 'articletags/SET_SEARCH_FILTER';
export const SET_ISFIRSTLOAD: string = 'articletags/SET_ISFIRSTLOAD';
export const RESET_FILTERS: string = 'articletags/RESET_FILTERS';
export const RESET_DATA: string = 'articletags/RESET_DATA';
export const SET_ITEMS_PAGE: string = 'articletags/SET_ITEMS_PAGE';

/** Initial State */
const initialState: IArticleTagsData = {
	items: [],
	isFetching: false,
	itemsPage: {
		currentPage: 0,
		totalPages: 0,
		totalItems: 0,
		itemsPerPage: 10,
		items: [],
	},
	keywordFilter: '',
	languageKeyFilter: '',
	isFirstLoad: false,
};

/** Reducer */
export function articleTagsReducer(state = initialState, action: IArticleTagsAction) {
	switch (action.type) {
		case GET_REQUEST:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case GET_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
				items: action.payload.items,
			});

		case GET_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case SET_ARTICLETAGS:
			return Object.assign({}, state, {
				items: action.payload.items,
			});

		case RESET_DATA:
			return Object.assign({}, state, {
				items: [],
				itemsPage: {
					currentPage: 0,
					totalPages: 0,
					totalItems: 0,
					itemsPerPage: 10,
					items: [],
				},
			});

		case RESET_FILTERS:
			return Object.assign({}, state, {
				keywordFilter: '',
				languageKeyFilter: '',
			});

		case SET_LANGUAGE_FILTER:
			return Object.assign({}, state, {
				languageKeyFilter: action.payload.languageKeyFilter,
				isFirstLoad: true,
			});

		case SET_SEARCH_FILTER:
			return Object.assign({}, state, {
				keywordFilter: action.payload.keywordFilter,
				isFirstLoad: true,
			});

		case SET_ISFIRSTLOAD:
			return Object.assign({}, state, {
				isFirstLoad: action.payload.isFirstLoad,
			});

		case SET_ITEMS_PAGE:
			return Object.assign({}, state, {
				itemsPage: action.payload.itemsPage,
			});

		default:
			return state;
	}
}

/** Action Creator */
export function searchArticleTags() {
	return (dispatch, getState: () => IStore) => {
		dispatch(articleTagsRequest());

		if (getState().articleTags.isFirstLoad) {
			dispatch(resetArticleTagsData());
			dispatch(setIsFirstLoad(false));
		}

		const currentPage: number = getState().articleTags.itemsPage.currentPage;
		const newPage: number = currentPage + 1;
		const itemsPerPage: number = getState().articleTags.itemsPage.itemsPerPage;

		let queryString = '?';
		queryString = queryString + '&currentPage=' + newPage;
		queryString = queryString + '&itemsPerPage=' + itemsPerPage;
		if (getState().articleTags.keywordFilter && getState().articleTags.keywordFilter !== '') {
			queryString = queryString + '&keyword=' + getState().articleTags.keywordFilter;
		}

		if (getState().articleTags.languageKeyFilter) {
			queryString = queryString + '&languageKey=' + getState().articleTags.languageKeyFilter;
		}

		const languages = getState().domainValues.languages;

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Article/SearchArticleTags' + queryString,
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res) => {
						const tagsState: IArticleTagsData = getState().articleTags;
						const items: IArticleTagDomainValue[] = tagsState.items;
						const newItems: IArticleTagDomainValue[] = res.items;
						Array.prototype.push.apply(items, newItems);

						const tags = items.map((tag) => {
							const lang = languages.filter((x) => x.key === tag.languageKey);
							if (!lang || lang.length === 0) {
								throw new Error('Language domain value not found');
							}
							return {
								key: tag.key,
								value: tag.value,
								languageKey: tag.languageKey,
								language: lang[0].value,
							};
						});

						dispatch(articleTagsSuccess(tags));
						dispatch(setItemsPage(res));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(articleTagsFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(articleTagsFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(articleTagsFailure(err));
			});
	};
}

/** Async Action Creator */
export function deleteArticleTag(id: number) {
	return (dispatch, getState: () => IStore) => {
		dispatch(articleTagsRequest());
		return fetch(
			getState().fetchDefault.baseAPI + 'api/Article/DeleteArticleTag/' + id,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'DELETE',
			}),
		)
			.then((res) => {
				if (res.ok) {
					dispatch(setIsFirstLoad(true));
					dispatch(searchArticleTags());
					dispatch(
						notifSend({
							message: i18n.t('Notification_Success_ArticleTag_Deleted'),
							kind: 'success',
							dismissAfter: 3000,
						}),
					);
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(articleTagsFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(articleTagsFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(articleTagsFailure(err));
			});
	};
}

/** Action Creator */
export function articleTagsRequest(): IArticleTagsAction {
	return {
		type: GET_REQUEST,
	};
}

/** Action Creator */
export function articleTagsSuccess(items: IArticleTagDomainValue[]): IArticleTagsAction {
	return {
		type: GET_SUCCESS,
		payload: {
			items,
		},
	};
}

/** Action Creator */
export function articleTagsFailure(message: any): IArticleTagsAction {
	return {
		type: GET_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function setArticleTags(items: IArticleTagDomainValue[]): IArticleTagsAction {
	return {
		type: SET_ARTICLETAGS,
		payload: {
			items,
		},
	};
}

/** Action Creator */
export function resetArticleTagsData(): IArticleTagsAction {
	return {
		type: RESET_DATA,
	};
}

export function setLanguageFilter(languageKeyFilter: string): IArticleTagsAction {
	return {
		type: SET_LANGUAGE_FILTER,
		payload: {
			languageKeyFilter,
		},
	};
}

export function setSearchFilter(keywordFilter: string): IArticleTagsAction {
	return {
		type: SET_SEARCH_FILTER,
		payload: {
			keywordFilter,
		},
	};
}

export function resetFilters(): IArticleTagsAction {
	return {
		type: RESET_FILTERS,
	};
}

export function setIsFirstLoad(isFirstLoad: boolean): IArticleTagsAction {
	return {
		type: SET_ISFIRSTLOAD,
		payload: {
			isFirstLoad,
		},
	};
}

/** Action Creator */
export function setItemsPage(itemsPage: IPage): IArticleTagsAction {
	return {
		type: SET_ITEMS_PAGE,
		payload: {
			itemsPage,
		},
	};
}
